import React from 'react';
/* eslint-disable sonarjs/no-duplicate-string */
import { PAGE_URLS } from 'consts/pageUrls';
import { StaticImage } from 'gatsby-plugin-image';
import { navInternalWithQueryString } from 'utils/marketingSafeDomain';

import { type BeforeAfterPopProps } from '../BeforeAfterPop';
import { type BeforeAfterCaseProps } from '../components/BeforeAfterCase';
import FiveStars from 'components/global/FiveStars';
import { LOCAL_COLORS } from 'styles/constants';

const sharedExamples: BeforeAfterCaseProps[] = [
  {
    summary: 'I recommend to everyone!!!!',
    quote:
      '“I’ve struggled with my skin for 10 years, and I have literally tried everything with no success. When I started Curology, my only goal was for it to take down a little inflammation. I never expected or hoped that something could work like this. I recommend to everyone!!!!!”',
    afterLabel: 'After 1 year',
    beforeImageMobile: (
      <StaticImage
        src="../images/example1/mobileBefore@1x.jpg"
        alt="Young brunette woman facing to the left, before Curology use."
        width={145}
      />
    ),
    beforeImageDesktop: (
      <StaticImage
        src="../images/example1/desktopBefore@2x.jpg"
        alt="Young brunette woman facing to the left, before Curology use."
        width={203}
      />
    ),
    afterImageMobile: (
      <StaticImage
        src="../images/example1/mobileAfter@1x.jpg"
        alt="Young brunette woman facing to the left, after Curology use for one year."
        width={145}
      />
    ),
    afterImageDesktop: (
      <StaticImage
        src="../images/example1/desktopAfter@2x.jpg"
        alt="Young brunette woman facing to the left, after Curology use for one year."
        width={203}
      />
    ),
  },
  {
    summary: 'My confidence came back 10 fold.',
    quote:
      "“I started using Curology after seeing before and afters on Instagram. And I'm SO THANKFUL I DID. I started waking up and no more acne. no more dark spots. My face felt lighter, less aged. My confidence came back 10 fold. It was truly amazing. Thank you Curology.”",
    afterLabel: 'After 3 months',
    beforeImageMobile: (
      <StaticImage
        src="../images/example3/mobileBefore@1x.jpg"
        alt="Brunette woman with curly hair facing to the left, before Curology use."
        width={145}
      />
    ),
    beforeImageDesktop: (
      <StaticImage
        src="../images/example3/desktopBefore@2x.jpg"
        alt="Brunette woman with curly hair facing to the left, before Curology use."
        width={203}
      />
    ),
    afterImageMobile: (
      <StaticImage
        src="../images/example3/mobileAfter@1x.jpg"
        alt="Brunette woman with curly hair facing to the left, after Curology use for three months."
        width={145}
      />
    ),
    afterImageDesktop: (
      <StaticImage
        src="../images/example3/desktopAfter@2x.jpg"
        alt="Brunette woman with curly hair facing to the left, after Curology use for three months."
        width={203}
      />
    ),
  },
];

export const POP_REDESIGN_BEFORE_AFTER_CONTENT: BeforeAfterPopProps = {
  header: 'The power of prescription treatment',
  subheader: '9 out of 10 members see clearer skin in just 3 weeks.*',
  persistentScrollBar: false,
  examples: [
    {
      summary: 'I recommend to everyone!!!!!',
      quote:
        '“I’ve struggled with my skin for 10 years, and I have literally tried everything with no success. When I started Curology, my only goal was for it to take down a little inflammation. I never expected or hoped that something could work like this. I recommend to everyone!!!!!”',
      afterLabel: 'After 1 year',
      beforeImageMobile: (
        <StaticImage
          src="../images/example1/mobileBefore@1x.jpg"
          alt="Young brunette woman facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example1/desktopBefore@2x.jpg"
          alt="Young brunette woman facing to the left, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example1/mobileAfter@1x.jpg"
          alt="Young brunette woman facing to the left, after Curology use for one year."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example1/desktopAfter@2x.jpg"
          alt="Young brunette woman facing to the left, after Curology use for one year."
          width={203}
        />
      ),
    },
    {
      summary: 'My skin has never looked better.',
      quote:
        '“I saw results in two weeks! The inflammation went down drastically and I was able to touch my face again. I told myself not to have such high expectations just in case it didn’t work for me. But look. My skin has never looked better.”',
      afterLabel: 'After 7 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example2/mobileBefore@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example2/desktopBefore@2x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example2/mobileAfter@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, after Curology use for seven months."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example2/desktopAfter@2x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, after Curology use for seven months."
          width={203}
        />
      ),
    },
    sharedExamples[1],
  ],
  ctaButton: {
    buttonText: 'Get my formula',
  },
  disclaimer:
    '*In a clinical trial of 150 Curology patients using a leave-on formula. Self-reported. Results may vary.',
};

export const ACNE_BEFORE_AFTER_CONTENT: BeforeAfterPopProps = {
  ...POP_REDESIGN_BEFORE_AFTER_CONTENT,
  examples: [
    ...sharedExamples,
    {
      summary: 'Fastest improvement I’ve ever seen',
      quote: 'January 2019 to March 2019...fastest improvement I’ve ever seen',
      afterLabel: 'After 3 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example4/mobileBefore@1x.jpg"
          alt="Brunette man facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example4/desktopBefore@2x.jpg"
          alt="Brunette man facing to the left, after Curology use for three months."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example4/mobileAfter@1x.jpg"
          alt="Brunette man facing to the left, before Curology use."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example4/desktopAfter@2x.jpg"
          alt="Brunette man facing to the left, after Curology use for three months."
          width={203}
        />
      ),
    },
  ],
  backgroundColor: LOCAL_COLORS.surfacePastelPurple,
};

const clickableAcneLogo = (
  <div
    css={['cursor: pointer;']}
    onClick={() =>
      navInternalWithQueryString(PAGE_URLS.products.customFormulaAcne)
    }
  >
    <StaticImage
      src="../images/customFormulaBottle@2x.jpg"
      alt="Bottle of Curology Custom Formula next to Custom Formula text"
    />
  </div>
);

const clickableHairLogo = (
  <div
    css={['cursor: pointer;']}
    onClick={() => navInternalWithQueryString(PAGE_URLS.products.hairFormula)}
  >
    <StaticImage
      src="../images/hairFormulaBottle@2x.jpg"
      alt="Bottle of Curology Hair Formula Rx next to Hair Formula text"
    />
  </div>
);

export const HOMEPAGE_BEFORE_AFTER_CONTENT: BeforeAfterPopProps = {
  header: 'Real patients. Real results.',
  subheader: (
    <div>
      <FiveStars /> 9k+ 5 star reviews<sup>▵</sup>
    </div>
  ),
  persistentScrollBar: true,
  examples: [
    {
      summary: 'I recommend to everyone!!!!',
      partner: false,
      quote:
        "I've struggled with my skin for 10 years, and I have literally tried everything with no success.",
      clickableLogo: clickableAcneLogo,
      afterLabel: 'After 1 year',
      beforeImageMobile: (
        <StaticImage
          src="../images/example1/mobileBefore@1x.jpg"
          alt="Young brunette woman facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example1/desktopBefore@2x.jpg"
          alt="Young brunette woman facing to the left, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example1/mobileAfter@1x.jpg"
          alt="Young brunette woman facing to the left, after Curology use for one year."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example1/desktopAfter@2x.jpg"
          alt="Young brunette woman facing to the left, after Curology use for one year."
          width={203}
        />
      ),
    },
    {
      summary: 'My skin has never looked better.',
      partner: false,
      quote:
        'The inflammation went down drastically and I was able to touch my face again.',
      clickableLogo: clickableAcneLogo,
      afterLabel: 'After 7 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example2/mobileBefore@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example2/desktopBefore@2x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example2/mobileAfter@1x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, after Curology use for seven months."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example2/desktopAfter@2x.jpg"
          alt="Brunette woman with curly hair and nose ring, facing to the left, after Curology use for seven months."
          width={203}
        />
      ),
    },
    {
      summary: "I'd highly recommend Hair Formula!",
      partner: true,
      quote:
        'After a couple of months of using it, I feel renewed and confident in my hairline!',
      clickableLogo: clickableHairLogo,
      afterLabel: 'After 1 month',
      beforeImageMobile: (
        <StaticImage
          src="../images/example5/mobileBefore@1x.jpg"
          alt="Curly-haired man's hairline, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example5/desktopBefore@2x.jpg"
          alt="Curly-haired man's hairline, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example5/mobileAfter@1x.jpg"
          alt="Curly-haired man's hairline, after 1 month of Curology use."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example5/desktopAfter@2x.jpg"
          alt="Curly-haired man's hairline, after 1 month of Curology use."
          width={203}
        />
      ),
    },
    {
      summary: 'I have noticed less shedding.',
      partner: true,
      quote:
        'In using Hair Formula Rx for the last 3.5 months, I have noticed less shedding and my hair is starting to feel thicker in volume overall. I am excited to see even more progress as time goes on!',
      clickableLogo: clickableHairLogo,
      afterLabel: 'After 3 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example6/mobileBefore@1x.jpg"
          alt="Brunette woman's hairline, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example6/desktopBefore@2x.jpg"
          alt="Brunette woman's hairline, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example6/mobileAfter@1x.jpg"
          alt="Brunette woman's hairline, after 3 months of Curology use."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example6/desktopAfter@2x.jpg"
          alt="Brunette woman's hairline, after 3 months of Curology use."
          width={203}
        />
      ),
    },
    {
      summary: 'My confidence came back 10 fold.',
      partner: false,
      quote: 'My face felt lighter, less aged.',
      clickableLogo: clickableAcneLogo,
      afterLabel: 'After 3 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example3/mobileBefore@1x.jpg"
          alt="Brunette woman with curly hair facing to the left, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example3/desktopBefore@2x.jpg"
          alt="Brunette woman with curly hair facing to the left, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example3/mobileAfter@1x.jpg"
          alt="Brunette woman with curly hair facing to the left, after Curology use for three months."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example3/desktopAfter@2x.jpg"
          alt="Brunette woman with curly hair facing to the left, after Curology use for three months."
          width={203}
        />
      ),
    },
    {
      summary: 'I wish this product was available when I was younger.',
      partner: true,
      quote:
        "After about 2 months of consistent use, I started to notice my hair wasn't appearing as thin as it used to, as well as some signs of new growth in the thinning areas around my hairline.",
      clickableLogo: clickableHairLogo,
      afterLabel: 'After 2 months',
      beforeImageMobile: (
        <StaticImage
          src="../images/example7/mobileBefore@1x.jpg"
          alt="Frontal view of brunette man's hairline, before Curology use."
          width={145}
        />
      ),
      beforeImageDesktop: (
        <StaticImage
          src="../images/example7/desktopBefore@2x.jpg"
          alt="Frontal view of brunette man's hairline, before Curology use."
          width={203}
        />
      ),
      afterImageMobile: (
        <StaticImage
          src="../images/example7/mobileAfter@1x.jpg"
          alt="Frontal view of brunette man's hairline, after 2 months of Curology use."
          width={145}
        />
      ),
      afterImageDesktop: (
        <StaticImage
          src="../images/example7/desktopAfter@2x.jpg"
          alt="Frontal view of brunette man's hairline, after 2 months of Curology use."
          width={203}
        />
      ),
    },
  ],
  ctaButton: {
    buttonText: 'Get started',
  },
};
